import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ACCESS_KEY } from "../config";

const AdminList = () => {
  // Lists and UI states
  const [admins, setAdmins] = useState([]); 
  const [countryHeads, setCountryHeads] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form States (Add New)
  const [selectedCountryHead, setSelectedCountryHead] = useState(''); 
  const [searchUniqueId, setSearchUniqueId] = useState('');
  const [fetchedUser, setFetchedUser] = useState(null); 
  const [searchMessage, setSearchMessage] = useState('');
  
  // Manual Input States (Common for Add/Edit)
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [codeNo, setCodeNo] = useState(''); 
  const [selectedId, setSelectedId] = useState(''); // Edit ke liye ID

  // 1. Load Initial Data (Admins and CountryHeads)
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const adminsRes = await axios.get(`https://admin.butterlive.co.in/management/getAdminUsers?start=1&limit=50&t=${Date.now()}`, {
        headers: { key: ACCESS_KEY }
      });
      if (adminsRes.data.status) {
        setAdmins(adminsRes.data.data);
      }

      const chRes = await axios.get(`https://admin.butterlive.co.in/management/getCountryHeads?start=1&limit=100`, {
        headers: { key: ACCESS_KEY }
      });
      if (chRes.data.status) {
        setCountryHeads(chRes.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // 2. MongoDB User Verification
  const handleUserSearch = async () => {
    if (!searchUniqueId) return alert("Please enter Unique ID first");
    setSearchMessage("Searching in MongoDB...");
    setFetchedUser(null);

    try {
      const response = await axios.get(`https://admin.butterlive.co.in/management/search-device-user?uniqueId=${searchUniqueId}`, {
        headers: { key: ACCESS_KEY }
      });
      if (response.data.status) {
        setFetchedUser(response.data.data);
        setSearchMessage("");
      } else {
        setSearchMessage("User not found in MongoDB!");
      }
    } catch (error) {
      setSearchMessage("Error fetching user data");
    }
  };

  // 3. Create New Admin
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCountryHead) return alert("Please select a Country Head first!");
    if (!fetchedUser) return alert("Please verify Unique ID from MongoDB first!");
    if (!phone || !email || !accountNo || !codeNo) return alert("Please fill all fields");

    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/addAdminUser`, {
        countryHeadId: selectedCountryHead,
        userId: fetchedUser._id || fetchedUser.id || fetchedUser.userId,
        uniqueId: searchUniqueId,
        codeNo: codeNo.toUpperCase().trim(), 
        phone: phone.trim(),
        email: email.trim(),
        accountNo: accountNo.trim()
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message || "Admin Created!");
        handleCloseModal();
        fetchInitialData(); 
      } else {
        alert(response.data.message || "Something went wrong");
      }
    } catch (error) {
      alert(error.response?.data?.message || "Error saving admin");
    }
  };

  // 🔥 4. Toggle Status (Active/Inactive)
  const handleToggleStatus = async (id) => {
    if (!window.confirm("Are you sure you want to change this Admin's status?")) return;
    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/toggleRoleStatus`, {
        id,
        role: "admin"
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message);
        fetchInitialData(); // Refresh table list
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert("Error updating status");
    }
  };

  // 🔥 5. Open Edit Modal with Data Pre-fill
  const handleOpenEdit = (admin) => {
    setSelectedId(admin._id);
    setPhone(admin.phone);
    setEmail(admin.email);
    setAccountNo(admin.accountNo);
    setShowEditModal(true);
  };

  // 🔥 6. Submit Updated Details
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !email) return alert("Phone and Email are required");

    try {
      const response = await axios.post(`https://admin.butterlive.co.in/management/updateRoleDetails`, {
        id: selectedId,
        role: "admin",
        phone: phone.trim(),
        email: email.trim(),
        accountNo: accountNo.trim()
      }, {
        headers: { key: ACCESS_KEY }
      });

      if (response.data.status) {
        alert(response.data.message || "Admin Details Updated!");
        setShowEditModal(false);
        clearForm();
        fetchInitialData(); 
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert("Error updating details");
    }
  };

  const clearForm = () => {
    setSelectedCountryHead('');
    setSearchUniqueId('');
    setFetchedUser(null);
    setPhone('');
    setEmail('');
    setAccountNo('');
    setCodeNo('');
    setSearchMessage('');
    setSelectedId('');
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    clearForm();
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Admin Management</h1>
      </div>

      <div className="top-bar">
        <input type="text" placeholder="Search Admin..." className="search-box" />
        <button onClick={() => setShowAddModal(true)} className="ban-btn ban-btn-unban" style={{ height: '40px', padding: '0 25px' }}>
          + New Admin
        </button>
      </div>

      <div className="table-card">
        <table width="100%" cellPadding="12">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Unique ID</th>
              <th>Code No</th>
              <th>Mapped Country Head</th>
              <th>Mobile</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="no-data">Loading Admins...</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan="9" className="no-data">No Admin created yet.</td></tr>
            ) : (
              admins.map((admin, index) => (
                <tr key={admin._id || index}>
                  <td>{index + 1}</td>
                  <td>
                    <img src={admin.image || "https://placehold.co/50"} alt="" className="user-avatar" onError={(e) => { e.target.src = "https://placehold.co/50" }} />
                  </td>
                  <td>{admin.name}</td>
                  <td>{admin.uniqueId}</td>
                  <td style={{ color: '#ff4d94', fontWeight: 'bold' }}>{admin.codeNo}</td>
                  <td>
                    <span style={{ color: '#9ca3af' }}>{admin.countryHeadName}</span> 
                    <span style={{ color: '#4f46e5', marginLeft: '5px', fontWeight: 'bold' }}>({admin.countryHeadCode})</span>
                  </td>
                  <td>{admin.phone}</td>
                  {/* Status Toggle on Click */}
                  <td onClick={() => handleToggleStatus(admin._id)} style={{ cursor: 'pointer' }}>
                    <span className={admin.isActive ? "status-active" : "status-banned"}>
                      {admin.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleOpenEdit(admin)} className="ban-btn" style={{ background: '#4f46e5' }}>Edit</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: '450px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#fff' }}>Create New Admin</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Select Country Head (Parent)</label>
              <select value={selectedCountryHead} onChange={(e) => setSelectedCountryHead(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }}>
                <option value="">-- Choose Country Head --</option>
                {countryHeads.map((ch) => (
                  <option key={ch._id} value={ch._id}>{ch.name} ({ch.codeNo})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px', background: '#0f172a', padding: '15px', borderRadius: '8px', border: '1px solid #1f2937' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Verify User (Unique ID)</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="number" placeholder="Enter User Unique ID" value={searchUniqueId} onChange={(e) => setSearchUniqueId(e.target.value)} style={{ flex: 1, padding: '10px', background: '#111827', color: 'white', border: '1px solid #374151', borderRadius: '6px', outline: 'none' }} />
                <button type="button" onClick={handleUserSearch} className="ban-btn ban-btn-active" style={{ background: '#4f46e5' }}>Verify</button>
              </div>
              {searchMessage && <p style={{ fontSize: '12px', color: '#ef4444', margin: '5px 0 0 0' }}>{searchMessage}</p>}
              {fetchedUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', background: '#111827', padding: '10px', borderRadius: '6px' }}>
                  <img src={fetchedUser.image || "https://placehold.co/40"} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#22c55e' }}>✔ User Verified</div>
                    <div style={{ fontSize: '13px', color: '#fff' }}>{fetchedUser.name}</div>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleCreateSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Code No</label>
                <input type="text" placeholder="e.g., AD0001" value={codeNo} onChange={(e) => setCodeNo(e.target.value)} maxLength={6} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Phone Number</label>
                <input type="text" placeholder="Enter Phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Email Address</label>
                <input type="email" placeholder="Enter Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Bank Account No</label>
                <input type="text" placeholder="Enter Account No" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleCloseModal} style={{ background: '#374151', border: '1px solid #4b5563' }}>Cancel</button>
                <button type="submit" className="ban-btn ban-btn-unban">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ width: '450px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#fff' }}>Edit Admin Details</h3>
            <form onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Phone Number</label>
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '5px' }}>Bank Account No</label>
                <input type="text" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} style={{ width: '100%', padding: '10px', background: '#0f172a', color: 'white', border: '1px solid #374151', borderRadius: '8px', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleCloseModal} style={{ background: '#374151', border: '1px solid #4b5563' }}>Cancel</button>
                <button type="submit" className="ban-btn ban-btn-unban" style={{ background: '#22c55e' }}>Update Details</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;
